import { UI } from './ui/ui';
import { CallManager } from './call/call-manager';
import { NotificationManager } from './notifications/notification-manager';
import { WebSocketClient } from './websocket/websocket-client';
import { registerServiceWorker } from './service-worker-registration';

interface AppState {
  currentChatId: string | null;
  userId: string | null;
  username: string | null;
  isConnected: boolean;
  activeCall: string | null;
  onlineUsers: Array<{userId: string, username: string}>;
}

class ChatApp {
  private ui: UI;
  private callManager: CallManager;
  private notificationManager: NotificationManager;
  private wsClient: WebSocketClient;
  private state: AppState;

  constructor() {
    this.state = {
      currentChatId: null,
      userId: null,
      username: null,
      isConnected: false,
      activeCall: null,
      onlineUsers: []
    };

    // Initialize modules
    this.ui = new UI();
    this.callManager = new CallManager();
    this.notificationManager = new NotificationManager();
    this.wsClient = new WebSocketClient();

    this.init();
  }

  private async init(): Promise<void> {
    console.log('🚀 Initializing Chat PWA...');

    // Register service worker
    await registerServiceWorker();

    // Setup event listeners
    this.setupEventListeners();

    // Initialize UI
    this.ui.init();

    // Request notification permission
    await this.notificationManager.requestPermission();

    // Connect to WebSocket server
    this.connectWebSocket();

    console.log('✅ Chat PWA initialized');
  }

  private setupEventListeners(): void {
    // UI Events
    this.ui.on('newChat', () => {
      this.handleNewChat();
    });

    this.ui.on('sendMessage', (message: string) => {
      this.handleSendMessage(message);
    });

    this.ui.on('selectChat', (chatId: string) => {
      this.handleSelectChat(chatId);
    });

    this.ui.on('voiceCall', (chatId: string) => {
      this.handleVoiceCall(chatId);
    });

    this.ui.on('videoCall', (chatId: string) => {
      this.handleVideoCall(chatId);
    });

    this.ui.on('endCall', () => {
      this.handleEndCall();
    });

    this.ui.on('answerCall', (callId: string) => {
      this.handleAnswerCall(callId);
    });

    this.ui.on('declineCall', (callId: string) => {
      this.handleDeclineCall(callId);
    });

    // WebSocket Events
    this.wsClient.on('connected', () => {
      this.handleWebSocketConnected();
    });

    this.wsClient.on('disconnected', () => {
      this.handleWebSocketDisconnected();
    });

    this.wsClient.on('message', (data: any) => {
      this.handleIncomingMessage(data);
    });

    this.wsClient.on('call-incoming', (data: any) => {
      this.handleIncomingCall(data);
    });

    this.wsClient.on('call-accepted', (data: any) => {
      this.handleCallAccepted(data);
    });

    this.wsClient.on('call-declined', (data: any) => {
      this.handleCallDeclined(data);
    });

    this.wsClient.on('call-ended', (data: any) => {
      this.handleCallEnded(data);
    });

    // Call Manager Events
    this.callManager.on('localStream', (stream: MediaStream) => {
      this.ui.setLocalStream(stream);
    });

    this.callManager.on('remoteStream', (stream: MediaStream) => {
      this.ui.setRemoteStream(stream);
    });

    this.callManager.on('callEnded', () => {
      this.ui.hideCallModal();
      this.state.activeCall = null;
    });

    // Service Worker Messages
    navigator.serviceWorker?.addEventListener('message', (event) => {
      this.handleServiceWorkerMessage(event.data);
    });
  }

  private connectWebSocket(): void {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    this.wsClient.connect(wsUrl);
  }

  private handleWebSocketConnected(): void {
    console.log('✅ WebSocket connected');
    this.state.isConnected = true;
    this.ui.showToast('Connected', 'success');
    
    // Register with server
    this.register();
  }

  private handleWebSocketDisconnected(): void {
    console.log('❌ WebSocket disconnected');
    this.state.isConnected = false;
    this.ui.showToast('Disconnected. Reconnecting...', 'error');
  }

  private async register(): Promise<void> {
    // Load user ID and username from localStorage or prompt
    let userId = localStorage.getItem('userId');
    let username = localStorage.getItem('username');
    
    if (!username) {
      username = prompt('Enter your username:');
      if (!username) {
        username = 'User' + Math.floor(Math.random() * 1000);
      }
      localStorage.setItem('username', username);
    }
    
    if (!userId) {
      userId = this.generateUserId();
      localStorage.setItem('userId', userId);
    }

    this.state.userId = userId;
    this.state.username = username;

    // Register with server
    this.wsClient.send({
      type: 'register',
      userId,
      username
    });
  }

  private generateUserId(): string {
    return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private handleNewChat(): void {
    console.log('➕ Creating new chat...');
    
    // Show online users to select from
    if (this.state.onlineUsers.length === 0) {
      this.ui.showToast('No users online', 'warning');
      return;
    }
    
    const userList = this.state.onlineUsers
      .filter(u => u.userId !== this.state.userId)
      .map(u => `${u.username} (${u.userId})`)
      .join('\\n');
    
    const selection = prompt(`Select a user to chat with:\\n\\n${userList}\\n\\nEnter username or user ID:`);
    if (selection) {
      // Find user by username or userId
      const user = this.state.onlineUsers.find(u => 
        u.username === selection || u.userId === selection
      );
      
      if (user) {
        this.handleSelectChat(user.userId);
        this.ui.showToast(`Chat with ${user.username}`, 'success');
      } else {
        this.ui.showToast('User not found', 'error');
      }
    }
  }

  private handleSelectChat(chatId: string): void {
    console.log('📱 Selected chat:', chatId);
    this.state.currentChatId = chatId;

    // Find the username for this user
    const user = this.state.onlineUsers.find(u => u.userId === chatId);
    const username = user ? user.username : chatId;

    // Show the chat window
    this.ui.showChatWindow(chatId, username, 'default-avatar.svg', 'online');
  }

  private handleSendMessage(message: string): void {
    if (!this.state.currentChatId || !message.trim()) return;

    const messageData = {
      type: 'message',
      to: this.state.currentChatId,
      text: message,
      timestamp: Date.now()
    };

    this.wsClient.send(messageData);
  }

  private handleIncomingMessage(data: any): void {
    console.log('Incoming message:', data);

    // Handle user list updates
    if (data.type === 'userList') {
      console.log('User list updated:', data.users);
      this.state.onlineUsers = data.users;
      this.updateChatList();
      return;
    }

    // Handle registration confirmation
    if (data.type === 'registered') {
      console.log('Registered as:', data.userId, data.username);
      this.ui.showToast(`Connected as ${data.username}`, 'success');
      return;
    }

    // Handle chat messages
    if (data.type === 'message') {
      const isFromSelf = data.self || data.from === this.state.userId;
      
      // Display message if we're in the correct chat
      const chatId = isFromSelf ? data.to : data.from;
      if (chatId === this.state.currentChatId) {
        this.ui.addMessage({
          id: `msg_${Date.now()}`,
          content: data.text,
          sender: isFromSelf ? 'me' : 'them',
          timestamp: data.timestamp || Date.now(),
          status: 'received'
        });
      }

      // Update chat list preview
      if (chatId) {
        const username = isFromSelf ? 'You' : (data.fromUsername || chatId);
        this.ui.updateChatPreview(chatId, `${username}: ${data.text}`, data.timestamp);
      }

      // Show notification for incoming messages (not self)
      if (!isFromSelf && (document.hidden || chatId !== this.state.currentChatId)) {
        this.notificationManager.showNotification({
          title: data.fromUsername || chatId,
          body: data.text,
          tag: `message-${Date.now()}`
        });
      }
    }
  }

  private updateChatList(): void {
    // Display all online users in the chat list
    this.state.onlineUsers.forEach(user => {
      if (user.userId !== this.state.userId) {
        this.ui.updateChatPreview(
          user.userId,
          `${user.username} is online`,
          Date.now(),
          user.username
        );
      }
    });
  }

  private async handleVoiceCall(chatId: string): Promise<void> {
    console.log('Starting voice call:', chatId);

    try {
      const stream = await this.callManager.startCall('audio');
      
      const callId = this.generateCallId();
      this.state.activeCall = callId;

      this.wsClient.send({
        type: 'call-initiate',
        callId,
        chatId,
        callType: 'audio'
      });

      this.ui.showCallModal({
        name: 'Contact',
        avatar: 'default-avatar.svg',
        status: 'Calling...',
        type: 'audio'
      });

    } catch (error) {
      console.error('Failed to start voice call:', error);
      this.ui.showToast('Failed to access microphone', 'error');
    }
  }

  private async handleVideoCall(chatId: string): Promise<void> {
    console.log('Starting video call:', chatId);

    try {
      const stream = await this.callManager.startCall('video');
      
      const callId = this.generateCallId();
      this.state.activeCall = callId;

      this.wsClient.send({
        type: 'call-initiate',
        callId,
        chatId,
        callType: 'video'
      });

      this.ui.showCallModal({
        name: 'Contact',
        avatar: 'default-avatar.svg',
        status: 'Calling...',
        type: 'video'
      });

    } catch (error) {
      console.error('Failed to start video call:', error);
      this.ui.showToast('Failed to access camera/microphone', 'error');
    }
  }

  private handleIncomingCall(data: any): void {
    console.log('Incoming call:', data);

    this.state.activeCall = data.callId;

    // Show incoming call UI
    this.ui.showIncomingCall({
      callId: data.callId,
      name: data.callerName || 'Unknown',
      avatar: data.callerAvatar || 'default-avatar.svg',
      type: data.callType
    });

    // Play ringtone
    this.ui.playRingtone();
  }

  private async handleAnswerCall(callId: string): Promise<void> {
    console.log('Answering call:', callId);

    try {
      // Get call type from stored data
      const callType = 'audio'; // Should be retrieved from call data

      const stream = await this.callManager.startCall(callType);

      this.wsClient.send({
        type: 'call-accept',
        callId
      });

      this.ui.hideIncomingCall();
      this.ui.stopRingtone();

      this.ui.showCallModal({
        name: 'Contact',
        avatar: 'default-avatar.svg',
        status: 'Connected',
        type: callType
      });

    } catch (error) {
      console.error('Failed to answer call:', error);
      this.ui.showToast('Failed to answer call', 'error');
      this.handleDeclineCall(callId);
    }
  }

  private handleDeclineCall(callId: string): void {
    console.log('Declining call:', callId);

    this.wsClient.send({
      type: 'call-decline',
      callId
    });

    this.ui.hideIncomingCall();
    this.ui.stopRingtone();
    this.state.activeCall = null;
  }

  private handleCallAccepted(data: any): void {
    console.log('Call accepted:', data);

    this.ui.updateCallStatus('Connected');

    // Exchange WebRTC signaling data
    this.callManager.handleRemotePeer(data);
  }

  private handleCallDeclined(data: any): void {
    console.log('Call declined:', data);

    this.ui.showToast('Call declined', 'info');
    this.handleEndCall();
  }

  private handleCallEnded(data: any): void {
    console.log('Call ended:', data);

    this.ui.showToast('Call ended', 'info');
    this.callManager.endCall();
    this.ui.hideCallModal();
    this.state.activeCall = null;
  }

  private handleEndCall(): void {
    console.log('Ending call');

    if (this.state.activeCall) {
      this.wsClient.send({
        type: 'call-end',
        callId: this.state.activeCall
      });
    }

    this.callManager.endCall();
    this.ui.hideCallModal();
    this.state.activeCall = null;
  }

  private generateCallId(): string {
    return `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private handleServiceWorkerMessage(data: any): void {
    console.log('Service Worker message:', data);

    switch (data.type) {
      case 'start-ringing':
        this.ui.playRingtone();
        break;
      case 'stop-ringing':
        this.ui.stopRingtone();
        break;
      case 'answer-call':
        this.handleAnswerCall(data.callId);
        break;
      case 'decline-call':
        this.handleDeclineCall(data.callId);
        break;
      case 'incoming-call':
        this.handleIncomingCall(data.data);
        break;
    }
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ChatApp();
  });
} else {
  new ChatApp();
}

export { ChatApp };
